import { useCallback, useMemo } from 'react';
import { TableWithHeader } from './shared/TableWithHeader';
import { DataTableRow } from '../../common/table/TableTypes';
import {
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../dependencies/types';
import { TableTypography } from '../../common/table/utils';
import { translateTypeToHumanName } from '../helpers';
import { ObjectTypeLabelAndKeyTag } from '../../common/ObjectTypeLabelAndKey';
import { TableItemHighlightedText } from '../dependencies/search/highlight/TableItemHighlightedText';
import { createStableLabelWrapper, markStyles } from '../dependencies/search/LabelWrapperComponent';
import { PossibleSectionInDependencies } from '../dependencies/search/types';
import { SalesforceItem } from '../../parser/ParserTypes';

const columns = [
  { field: 'label', headerName: 'Label', width: '140px', minWidth: '140px' },
  { field: 'parentType', headerName: 'Type', width: '100px', minWidth: '100px' },
  { field: 'objectName', headerName: 'Object', width: '120px', minWidth: '120px' },
];

const renderRows = ({
  items,
  ObjectNameLabelWrapper,
  section = 'dependencies',
  dataTableType,
}: {
  items: SalesforceItem[];
  ObjectNameLabelWrapper: ReturnType<typeof createStableLabelWrapper>;
  section?: PossibleSectionInDependencies;
  dataTableType: string;
}) => {
  return items.map((row, index) => {
    return {
      id: row.id,
      name: row.name,
      label: (
        <TableTypography>
          <TableItemHighlightedText
            text={row.label}
            field="label"
            section={section}
            tableType={dataTableType}
            rowIndex={index}
          />
        </TableTypography>
      ),
      parentType: (
        <TableTypography>
          <TableItemHighlightedText
            text={translateTypeToHumanName(row.parentType)}
            field="parentType"
            section={section}
            tableType={dataTableType}
            rowIndex={index}
          />
        </TableTypography>
      ),
      objectName: row.objectApiNames?.[0] ? (
        <ObjectTypeLabelAndKeyTag
          objectType={row.objectApiNames?.[0]}
          LabelWrapperComponent={ObjectNameLabelWrapper}
          labelWrapperComponentSx={markStyles}
          labelWrapperComponentProps={{ rowIndex: index }}
        />
      ) : (
        <TableTypography>N/A</TableTypography>
      ),
      _parentType: row.parentType,
      dependencyType: 'fieldType' in row ? (row.fieldType ?? '') : '',
      isClickable: true,
    };
  });
};

interface RelatedObjectsTableProps {
  items: SalesforceItem[];
  onItemClick: (props: SelectedDependencyWithClearHistoryProps) => void;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
  dataTableType?: string;
}

export const RelatedObjectsTable = ({
  items,
  onItemClick,
  clearHistory,
  section = 'dependencies',
  dataTableType = 'relatedObjects',
}: RelatedObjectsTableProps) => {
  // Create stable wrapper component ONCE per table, not per row
  const ObjectNameLabelWrapper = useMemo(
    () =>
      createStableLabelWrapper({
        field: 'objectApiNames',
        section,
        tableType: dataTableType,
      }),
    [section, dataTableType],
  );

  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      onItemClick({
        id: row.id,
        name: row._nameStr,
        parentType: row._parentType,
        dependencyType: row.dependencyType,
        clearHistory,
        contentType: DependenciesContentViewType.default,
      });
    },
    [onItemClick, clearHistory],
  );

  return (
    <TableWithHeader
      tableHeader="Related Objects"
      columns={columns}
      rows={renderRows({ items, ObjectNameLabelWrapper, section, dataTableType })}
      onRowClick={onRowClick}
      dataTableType={dataTableType}
      section={section}
    />
  );
};
